import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Menu, X, LogIn } from 'lucide-react';
import { cn } from '../../lib/utils';
import { siteLogo, SITE_NAME } from '../../config/branding';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, currentUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();

  const handleMobileNav = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (totalHeight > 0) {
        const progress = (window.scrollY / totalHeight) * 100;
        setScrollProgress(progress);
      } else {
        setScrollProgress(0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToCourses = () => {
    const coursesSection = document.getElementById('courses-section');
    if (coursesSection) {
      coursesSection.scrollIntoView({ behavior: 'smooth' });
    } else if (location.pathname !== '/') {
      window.location.href = '/#courses-section';
    }
  };

  const linkBase =
    "relative px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300";
  /* Same language as hero overlay + «اكتشف المنصة» CTA (white on translucent emerald/black) */
  const linkIdle =
    "text-white/90 hover:text-white hover:bg-white/10";
  const linkActive =
    "text-white bg-amber-400/20 ring-1 ring-amber-400/55 shadow-sm shadow-black/20";

  return (
    <>
      <nav
        dir="rtl"
        className="fixed inset-x-0 top-0 z-40 h-16 border-b-2 border-amber-400/70 bg-emerald-900 shadow-lg shadow-black/25 transition-colors duration-300"
      >
        <div className="relative h-full">
          <div className="max-w mx-auto px-3 sm:px-4 lg:px-6 h-full relative overflow-hidden">
            <div className="absolute inset-x-3 sm:inset-x-4 lg:inset-x-6 bottom-0 h-0.5 rounded-full overflow-hidden opacity-100">
              <div
                className="h-full bg-gradient-to-l from-amber-400 via-amber-500 to-yellow-400 transition-all duration-300 ease-out rounded-full"
                style={{ width: `${scrollProgress}%` }}
              />
            </div>
            <div className="relative flex items-center justify-between h-full">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-auto">
                <div className="flex-shrink-0">
                  <Link to="/" className="flex items-center justify-center group">
                    <img
                      src={siteLogo}
                      alt={SITE_NAME}
                      className={cn(
                        "w-auto object-contain transition-all duration-500 group-hover:scale-105 drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]",
                        "h-10 sm:h-11 md:h-12"
                      )}
                    />
                  </Link>
                </div>
              </div>

              <div className="flex items-center flex-1 min-w-0 justify-start">
                <div className="hidden lg:flex lg:items-center lg:gap-2 lg:space-x-reverse">
                  <Link
                    to="/"
                    className={cn(
                      linkBase,
                      location.pathname === '/' ? linkActive : linkIdle
                    )}
                  >
                    الرئيسية
                    {location.pathname === '/' && (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-amber-400 rounded-full" />
                    )}
                  </Link>
                  <button
                    type="button"
                    onClick={scrollToCourses}
                    className={cn(linkBase, linkIdle, "cursor-pointer")}
                  >
                    المناهج
                  </button>
                  {isAuthenticated && (
                    <Link
                      to="/enrolled-courses"
                      className={cn(
                        linkBase,
                        location.pathname === '/enrolled-courses' ? linkActive : linkIdle
                      )}
                    >
                      مناهجي
                      {location.pathname === '/enrolled-courses' && (
                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-amber-400 rounded-full" />
                      )}
                    </Link>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 justify-end flex-1 min-w-0">
                <div className="hidden lg:flex lg:items-center lg:gap-3">
                  {isAuthenticated ? (
                    <div className="relative mr-3 flex items-center space-x-3">
                      <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl border-2 border-amber-400/50 bg-emerald-950/80 shadow-sm group hover:border-amber-400/70 transition-all duration-300">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                          <svg className="w-5 h-5 text-emerald-950" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-white/70 leading-none">مرحباً</span>
                          <span className="text-sm font-bold text-white mt-0.5">
                            {currentUser?.name}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={logout}
                        className="px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 active:scale-95 group flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        تسجيل الخروج
                      </button>
                    </div>
                  ) : (
                    <>
                      <Link
                        to="/register"
                        className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 text-emerald-950 text-sm font-bold rounded-2xl shadow-lg shadow-black/30 ring-2 ring-amber-200/50 hover:brightness-105 hover:scale-105 active:scale-95 transition-all duration-300"
                      >
                        <Users size={18} className="group-hover:scale-110 transition-transform" />
                        <span>إنشاء حساب</span>
                      </Link>
                      <Link
                        to="/login"
                        className="group flex items-center gap-2 px-5 py-2.5 bg-emerald-950 border-2 border-amber-400/70 text-white text-sm font-bold rounded-2xl shadow-lg hover:bg-emerald-950/90 hover:scale-105 active:scale-95 transition-all duration-300"
                      >
                        <LogIn size={18} className="group-hover:translate-x-1 transition-transform" />
                        <span>سجل دخول</span>
                      </Link>
                    </>
                  )}
                </div>

                <div className="flex items-center lg:hidden">
                  <button
                    type="button"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="inline-flex items-center justify-center rounded-xl p-2.5 text-white hover:bg-white/15 hover:text-white active:bg-white/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400/40 touch-manipulation"
                    aria-label={isMobileMenuOpen ? "إغلاق القائمة" : "فتح القائمة"}
                    aria-expanded={isMobileMenuOpen}
                  >
                    {isMobileMenuOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div
              className="lg:hidden fixed inset-x-0 top-16 z-50 border-t-2 border-amber-400/50 bg-emerald-900 shadow-2xl shadow-black/40"
            >
              <div className="space-y-1 px-4 pt-4 pb-6 max-h-[calc(100vh-5rem)] overflow-y-auto">
                <Link
                  to="/"
                  onClick={handleMobileNav}
                  className={cn(
                    "block rounded-xl px-4 py-3.5 text-base font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation",
                    location.pathname === '/'
                      ? "text-white bg-amber-400/20 border-r-4 border-amber-400"
                      : "text-white/90 active:bg-white/10"
                  )}
                >
                  الرئيسية
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    handleMobileNav();
                    scrollToCourses();
                  }}
                  className="block w-full text-right rounded-xl px-4 py-3.5 text-base font-semibold text-white/90 active:bg-white/10 active:scale-[0.98] transition-all duration-200 touch-manipulation"
                >
                  المناهج
                </button>
                {isAuthenticated && (
                  <Link
                    to="/enrolled-courses"
                    onClick={handleMobileNav}
                    className={cn(
                      "block rounded-xl px-4 py-3.5 text-base font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation",
                      location.pathname === '/enrolled-courses'
                        ? "text-white bg-amber-400/20 border-r-4 border-amber-400"
                        : "text-white/90 active:bg-white/10"
                    )}
                  >
                    مناهجي
                  </Link>
                )}

                <div className="border-t border-amber-400/35 my-4" />

                {isAuthenticated ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-amber-400/50 bg-emerald-950/90">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-md flex-shrink-0">
                        <svg className="w-5 h-5 text-emerald-950" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/65 mb-0.5">مرحباً</p>
                        <p className="text-sm font-bold text-white truncate">{currentUser?.name}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={logout}
                      className="block w-full text-center rounded-xl bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3.5 text-base font-semibold shadow-md active:scale-[0.98] active:shadow-sm transition-all duration-200 touch-manipulation"
                    >
                      تسجيل الخروج
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Link
                      to="/register"
                      onClick={handleMobileNav}
                      className="block w-full text-center rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 text-emerald-950 px-4 py-3.5 text-base font-bold shadow-md ring-1 ring-amber-200/50 active:scale-[0.98] transition-all duration-200 touch-manipulation"
                    >
                      إنشاء حساب جديد
                    </Link>
                    <Link
                      to="/login"
                      onClick={handleMobileNav}
                      className="block w-full text-center rounded-xl bg-emerald-950 border-2 border-amber-400/70 text-white px-4 py-3.5 text-base font-bold shadow-lg hover:bg-emerald-950/90 active:scale-[0.98] transition-all duration-200 touch-manipulation"
                    >
                      سجل الدخول
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
